/**
 * frontend/src/pages/valuation/_useTuningPreview.ts
 *
 * The debounced live recompute/preflight loop for ValuationTuning.tsx, split
 * out under docs/FILE_SIZE_POLICY.md (the screen file crossed the 400-line
 * hard-stop threshold once D2/D3/D6 draft wiring landed). Owns exactly the
 * pair of calls and the state they fill — nothing about industry, earnings,
 * or knobs, which stay on the screen and feed in as `body`.
 *
 * `/recompute` never clamps; `/preflight` rides the same debounce so the
 * blocker list can never describe a different set of inputs than the number
 * on screen. D2 — a settled recompute is also the trigger to save the
 * working draft, via the `saveDraft` passed in from `useValuationDraft`.
 *
 * VAL-INCOME-BASIS-FIX-1 — this hook never reconstructs the income leg and
 * `RecomputeBody` carries no `replacement_compensation` field of its own.
 * The wage deduction happens once, in Intake's `/run`; the result lands in
 * `body.income_value` (an already-computed, opaque figure) via the shared
 * draft (see `_valuationTuningHelpers.ts` D6 and `ValuationIntake.tsx`'s
 * post-run `saveDraft({ income_value: result.triangulation.income_value })`).
 * Because this hook forwards `body` verbatim to `/recompute` and `/preflight`
 * with no re-derivation, the Live preview sends the same wage-adjusted value
 * the run just produced — with nothing to wire here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { preflightValuation, recomputeValuation } from '@/api/valuationB3'
import type { BasisBlock, ComputedPayload, RecomputeBody } from '@/types/valuationB3'
import type { SaveDraftBody } from '@/types/valuationDraft'

export interface UseTuningPreview {
  computed: ComputedPayload | null
  basis: BasisBlock | null
  blockers: string[]
  warnings: string[]
  runError: string | null
  /** VAL-NAV-STATE-1 R3.11 — the working draft's save is now awaited (below)
   * instead of fired-and-forgotten; this is its error, for the screen to
   * surface. Distinct from `runError`, which is the engine's, not the
   * draft's. */
  saveError: string | null
  pending: boolean
}

export const RATES_MISSING_MESSAGE =
  'Fill in Risk-free rate, Company-specific risk, Long-term growth rate and DCF growth rate on Intake first.'

function draftPatchFrom(payload: RecomputeBody): SaveDraftBody {
  return {
    benchmark_key: payload.benchmark_key,
    normalized_earnings: payload.normalized_earnings,
    driver_answers: payload.driver_answers,
    naics_code: payload.naics_code,
    sub_vertical: payload.sub_vertical,
    market_only_acknowledged: payload.market_only_acknowledged,
    // VAL-INPUT-PERSIST-1E — always sent, so Reset (no knobs, no reason)
    // clears the saved tuning; a reason alone is kept as {rationale}.
    tuning: payload.tuning ?? (payload.rationale ? { rationale: payload.rationale } : null),
  }
}

export function useTuningPreview(
  id: string | undefined,
  ready: boolean,
  body: RecomputeBody | null,
  // VAL-NAV-STATE-1 R3.11 — `useValuationDraft`'s save() resolves the error
  // message on failure (was Promise<void>); this hook now awaits it and
  // exposes the result as `saveError` instead of firing it and forgetting.
  saveDraft: (body: SaveDraftBody) => Promise<string | null>,
  // VAL-INTAKE-BLANK-JUDGMENT-1 C2 — a rate is blank on Intake: ask the engine
  // for nothing, still save the working draft.
  ratesMissing = false,
): UseTuningPreview {
  const [computed, setComputed] = useState<ComputedPayload | null>(null)
  const [basis, setBasis] = useState<BasisBlock | null>(null)
  const [blockers, setBlockers] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [runError, setRunError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const seq = useRef(0)
  const runPreview = useCallback(
    (payload: RecomputeBody) => {
      if (!id) return
      const mine = ++seq.current
      setPending(true)
      Promise.all([
        recomputeValuation(id, payload),
        preflightValuation(id, payload).catch(() => null),
      ])
        .then(async ([rc, pf]) => {
          if (mine !== seq.current) return // a newer keystroke already won
          const { valuation_engagement_id: _id, value_kind: _k, persisted: _p, ...rest } = rc
          setComputed(rest as ComputedPayload)
          setBasis({
            config_version: rc.config_version,
            config_source: rc.config_source,
            config_fingerprint: rc.config_fingerprint,
            tuned: rc.tuned,
            tuning_rationale_recorded: rc.tuning_rationale_recorded,
          })
          setBlockers(pf ? pf.blockers : [])
          setWarnings(pf ? pf.warnings : [])
          setRunError(null)
          // D2 — save on a settled Recompute, not on every character; this
          // debounce is the same one gating the recompute call itself.
          // R3.11 — awaited so a save failure is known, not silently lost.
          const err = await saveDraft(draftPatchFrom(payload))
          if (mine === seq.current) setSaveError(err)
        })
        .catch(err => {
          if (mine !== seq.current) return
          setRunError(
            err?.response?.data?.error?.message ??
              'The engine refused these inputs. Adjust and try again.',
          )
        })
        .finally(() => {
          if (mine === seq.current) setPending(false)
        })
    },
    [id, saveDraft],
  )

  useEffect(() => {
    if (!ready || !body) return
    if (ratesMissing) {
      const t = window.setTimeout(async () => {
        const err = await saveDraft(draftPatchFrom(body))
        setSaveError(err)
      }, 350)
      return () => window.clearTimeout(t)
    }
    const t = window.setTimeout(() => runPreview(body), 350)
    return () => window.clearTimeout(t)
  }, [ready, body, runPreview, ratesMissing, saveDraft])

  return {
    computed, basis, blockers, warnings, saveError, pending,
    runError: ready && ratesMissing ? RATES_MISSING_MESSAGE : runError,
  }
}
